import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { apiErrorMessage, authService } from '../../services/api';
import { Reveal } from '../../motion';

/**
 * Request a password reset link.
 *
 * The confirmation is deliberately the same whether or not the address has an
 * account — the API answers identically, and saying "no account found" here
 * would give away who is registered.
 */
const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await authService.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      // Only a transport or rate-limit failure reaches here.
      setError(apiErrorMessage(err, 'Could not send the reset link. Try again shortly.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 8 }}>
      <Container maxWidth="sm">
        <Reveal>
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
            {sent ? (
              <Stack spacing={2.5}>
                <Typography variant="h5" fontWeight={700}>
                  Check your email
                </Typography>
                <Typography color="text.secondary">
                  If <strong>{email.trim()}</strong> has an account, a reset link is on
                  its way. It expires in 30 minutes and can only be used once.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nothing arriving? Check the spam folder, or{' '}
                  <Link
                    component="button"
                    type="button"
                    fontWeight={600}
                    onClick={() => setSent(false)}
                  >
                    try a different address
                  </Link>
                  .
                </Typography>
                <Button component={RouterLink} to="/login" variant="outlined" fullWidth>
                  Back to sign in
                </Button>
              </Stack>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Reset your password
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Enter the email address on your account and we&apos;ll send you a link
                  to choose a new password.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2.5 }}>
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  required
                  autoFocus
                  type="email"
                  label="Email address"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>

                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Remembered it?{' '}
                  <Link component={RouterLink} to="/login" fontWeight={600}>
                    Sign in
                  </Link>
                </Typography>
              </Box>
            )}
          </Paper>
        </Reveal>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
